/**
 * V80: 인사관리 > 퇴사자 관리 페이지
 *
 * terminatedEmployees 스냅샷 기반으로 퇴사자 목록을 표시합니다.
 *
 * **V80 변경사항:**
 * - 날짜 필터링을 서버 사이드로 전환 (성능/정합성 개선)
 * - getTerminatedEmployees에 leaveDateFrom/leaveDateTo 파라미터 전달
 *
 * **V79 변경사항:**
 * - 초기 진입 시 데이터 미노출 (날짜 검색 필수)
 * - 퇴사일 기간 검색 (from-to) + 텍스트 검색
 * - 컬럼 재구성: 이름, 사번, 조직, 직위, 직책, 퇴사일, 고용형태, 퇴사사유, 근속일
 * - 상세 버튼 제거, 이름 클릭 시 상세 모달 오픈
 * - 근속일(일수) 계산 표시
 *
 * **권한:** HR(role_hr) 또는 system_admin
 */

import React, { useState, useMemo, useCallback } from 'react';
import { ModalOverlay } from '../../shared/components/ModalOverlay';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase/config';
import {
  Search,
  Loader,
  AlertCircle,
  User,
  Building2,
  Calendar,
  FileText,
  X,
  Users,
  RotateCcw,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';

// ============================================
// 타입 정의 (백엔드 스키마와 동기화)
// ============================================

interface BasicInfoSnapshot {
  nameKo: string;
  email: string;
  phoneNumber?: string;
  gender?: string;
  birthDate?: string;
}

interface OrgSnapshot {
  orgUnitId: string | null;
  orgUnitName: string;
  orgPathNames?: string[];
}

interface PositionSnapshot {
  positionId: string | null;
  positionName: string;
}

interface TitleSnapshot {
  titleId: string | null;
  titleName: string;
}

interface EmploymentSnapshot {
  employmentTypeCode: string;
  employmentTypeName: string;
  employeeNumber: string;
  hireDate: string | null;
  ngleFirstHireDate: string | null;
  contractEndDate: string | null;
  probationEndDate: string | null;
}

interface TerminationInfo {
  terminatedAt: any;
  leaveDate: string;
  reasonCode?: string;
  reasonName?: string;
  reasonNote?: string;
  terminatedBy: string;
  terminatedByName?: string;
  terminationSource: 'batch' | 'manual' | 'backfill';
}

/**
 * V70.2: 퇴사자 상세 추가 정보 (getTerminatedEmployeeDetail 확장 응답)
 * - 목록(getTerminatedEmployees) 응답에는 없고 상세 조회에서만 채워진다.
 */
interface TerminatedAdditionalInfo {
  /** 과거 포함 파견(DISPATCH) 이력 존재 여부 */
  hasDispatchHistory: boolean;
  dispatchHistoryCount: number;
  /** 퇴사 시점 고용형태가 파견이었는지 */
  terminatedAsDispatch: boolean;
  /** users.affiliationName — 파견 이력이 없으면 null */
  dispatchCompanyName: string | null;
  /** 원문. 권한 없으면 null (BE에서 checkIsAdminLike로 차단) */
  residentRegistrationNo: string | null;
  hasSensitiveAccess: boolean;
}

interface TerminatedEmployee {
  id: string;
  basicInfo: BasicInfoSnapshot;
  orgSnapshot: OrgSnapshot;
  positionSnapshot: PositionSnapshot;
  titleSnapshot: TitleSnapshot;
  employmentSnapshot: EmploymentSnapshot;
  termination: TerminationInfo;
  schemaVersion: number;
  createdAt: any;
  updatedAt: any;
  /** V70.2: 상세 조회에서만 채워짐 */
  additionalInfo?: TerminatedAdditionalInfo;
}

// ============================================
// 헬퍼 함수
// ============================================

/**
 * 근속일 계산 (입사일 ~ 퇴사일)
 * 같은 날 입사/퇴사면 1일로 계산
 */
function calculateTenureDays(hireDate: string | null | undefined, leaveDate: string | null | undefined): number | null {
  if (!hireDate || !leaveDate) return null;

  try {
    const hire = new Date(hireDate + 'T00:00:00');
    const leave = new Date(leaveDate + 'T00:00:00');

    if (isNaN(hire.getTime()) || isNaN(leave.getTime())) return null;

    const diffTime = leave.getTime() - hire.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    // 같은 날이면 1일, 아니면 차이 + 1
    return diffDays + 1;
  } catch {
    return null;
  }
}

// V80: isDateInRange 제거됨 (서버 사이드 필터링으로 전환)

/**
 * 오늘 날짜 문자열 (YYYY-MM-DD)
 */
function getTodayString(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * 올해 1월 1일 문자열
 */
function getYearStartString(): string {
  const now = new Date();
  return `${now.getFullYear()}-01-01`;
}

// ============================================
// 메인 컴포넌트
// ============================================

export function HrTerminatedEmployeesPage() {
  // ========================================
  // State
  // ========================================

  // 검색 조건
  const [dateFrom, setDateFrom] = useState<string>(getYearStartString());
  const [dateTo, setDateTo] = useState<string>(getTodayString());
  const [searchText, setSearchText] = useState<string>('');

  // 조회 결과
  const [allEmployees, setAllEmployees] = useState<TerminatedEmployee[]>([]); // 서버에서 가져온 전체 데이터
  const [dateFilteredEmployees, setDateFilteredEmployees] = useState<TerminatedEmployee[]>([]); // 날짜 필터 적용 결과

  // UI 상태
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false); // 검색 실행 여부

  // 상세 모달
  const [selectedEmployee, setSelectedEmployee] = useState<TerminatedEmployee | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // ========================================
  // 검색 실행
  // ========================================

  const handleSearch = useCallback(async () => {
    // 날짜 입력 검증
    if (!dateFrom) {
      setError('시작일을 선택해주세요.');
      return;
    }

    // to가 없으면 from과 동일하게 (하루 검색)
    const effectiveTo = dateTo || dateFrom;

    if (dateFrom > effectiveTo) {
      setError('시작일은 종료일보다 이전이어야 합니다.');
      return;
    }

    if (!functions) {
      setError('Firebase가 초기화되지 않았습니다.');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const getTerminatedEmployees = httpsCallable<
        { searchQuery?: string; limit?: number; leaveDateFrom?: string; leaveDateTo?: string },
        { success: boolean; employees: TerminatedEmployee[]; totalCount: number }
      >(functions, 'getTerminatedEmployees');

      // V80: 서버 사이드 날짜 필터링으로 전환
      const result = await getTerminatedEmployees({
        limit: 1000,
        leaveDateFrom: dateFrom,
        leaveDateTo: effectiveTo,
      });

      if (result.data.success) {
        // V80: 서버에서 이미 날짜 필터링 완료
        const filtered = result.data.employees;
        setAllEmployees(filtered);
        setDateFilteredEmployees(filtered);
        setHasSearched(true);
      } else {
        throw new Error('퇴사자 목록 조회 실패');
      }
    } catch (err: any) {
      console.error('[V79] 퇴사자 목록 조회 실패:', err);
      setError(err.message || '퇴사자 목록을 불러오는 중 오류가 발생했습니다.');
    } finally {
      setIsLoading(false);
    }
  }, [dateFrom, dateTo]);

  // ========================================
  // 텍스트 검색 (날짜 결과 내에서 필터링)
  // ========================================

  const filteredEmployees = useMemo(() => {
    if (!searchText.trim()) return dateFilteredEmployees;

    const q = searchText.trim().toLowerCase();
    return dateFilteredEmployees.filter((emp) => {
      const nameKo = emp.basicInfo?.nameKo?.toLowerCase() || '';
      const email = emp.basicInfo?.email?.toLowerCase() || '';
      const employeeNumber = emp.employmentSnapshot?.employeeNumber?.toLowerCase() || '';
      const orgName = emp.orgSnapshot?.orgUnitName?.toLowerCase() || '';

      return (
        nameKo.includes(q) ||
        email.includes(q) ||
        employeeNumber.includes(q) ||
        orgName.includes(q)
      );
    });
  }, [dateFilteredEmployees, searchText]);

  // ========================================
  // 검색어 초기화 (날짜 결과는 유지)
  // ========================================

  const handleClearSearchText = () => {
    setSearchText('');
  };

  // V60: 상세 조회 (enriched data)
  const handleShowDetail = useCallback(async (emp: TerminatedEmployee) => {
    if (!functions) {
      setSelectedEmployee(emp);
      return;
    }
    setDetailLoading(true);
    try {
      const getDetail = httpsCallable<
        { userId: string },
        { success: boolean; employee: TerminatedEmployee | null }
      >(functions, 'getTerminatedEmployeeDetail');
      const result = await getDetail({ userId: emp.id });
      setSelectedEmployee(result.data.employee || emp);
    } catch {
      // fallback to list data
      setSelectedEmployee(emp);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  // ========================================
  // 렌더링
  // ========================================

  return (
    <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
      {/* 페이지 헤더 */}
      <div style={{ marginBottom: '24px' }}>
        <h1
          style={{
            fontSize: '24px',
            fontWeight: 'bold',
            color: 'var(--color-gray-800)',
            margin: 0,
          }}
        >
          퇴사자 관리
        </h1>
        <p style={{ fontSize: '14px', color: 'var(--color-gray-500)', marginTop: '4px' }}>
          퇴사일 기간을 선택하여 퇴사자 목록을 조회합니다.
        </p>
      </div>

      {/* 검색 패널 */}
      <div
        style={{
          background: 'white',
          borderRadius: '8px',
          padding: '20px',
          marginBottom: '24px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        }}
      >
        {/* 퇴사일 검색 */}
        <div style={{ marginBottom: '16px' }}>
          <label
            style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '600',
              color: 'var(--color-gray-700)',
              marginBottom: '8px',
            }}
          >
            퇴사일 검색 (필수)
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              style={{
                padding: '10px 14px',
                border: '1px solid var(--color-gray-200)',
                borderRadius: '8px',
                fontSize: '14px',
                width: '160px',
              }}
            />
            <span style={{ color: 'var(--color-gray-500)' }}>~</span>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              style={{
                padding: '10px 14px',
                border: '1px solid var(--color-gray-200)',
                borderRadius: '8px',
                fontSize: '14px',
                width: '160px',
              }}
            />
            <button
              onClick={handleSearch}
              disabled={isLoading}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 24px',
                background: isLoading ? 'var(--color-gray-300)' : 'var(--color-primary)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '600',
              }}
            >
              {isLoading ? (
                <>
                  <Loader size={16} style={{ animation: 'spin 1s linear infinite' }} />
                  조회 중...
                </>
              ) : (
                <>
                  <Search size={16} />
                  검색
                </>
              )}
            </button>
          </div>
        </div>

        {/* 텍스트 검색 */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            paddingTop: '16px',
            borderTop: '1px solid var(--color-gray-100)',
          }}
        >
          <div style={{ position: 'relative', flex: 1, maxWidth: '400px' }}>
            <Search
              size={18}
              style={{
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'var(--color-gray-400)',
              }}
            />
            <input
              type="text"
              placeholder="이름/사번/이메일/조직 검색"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              disabled={!hasSearched}
              style={{
                width: '100%',
                padding: '10px 12px 10px 40px',
                border: '1px solid var(--color-gray-200)',
                borderRadius: '8px',
                fontSize: '14px',
                background: hasSearched ? 'white' : 'var(--color-gray-50)',
              }}
            />
          </div>
          {searchText && (
            <button
              onClick={handleClearSearchText}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                padding: '8px 12px',
                background: 'transparent',
                border: '1px solid var(--color-gray-200)',
                borderRadius: '6px',
                color: 'var(--color-gray-600)',
                cursor: 'pointer',
                fontSize: '14px',
              }}
            >
              <RotateCcw size={14} />
              검색어 초기화
            </button>
          )}
        </div>
      </div>

      {/* 에러 메시지 */}
      {error && (
        <div
          style={{
            padding: '16px',
            marginBottom: '24px',
            background: '#fef2f2',
            border: '1px solid #ef4444',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            color: '#dc2626',
          }}
        >
          <AlertCircle size={20} />
          <span>{error}</span>
          <button
            onClick={() => setError(null)}
            style={{
              marginLeft: 'auto',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
            }}
          >
            <X size={16} color="#dc2626" />
          </button>
        </div>
      )}

      {/* 초기 안내 (검색 전) */}
      {!hasSearched && !isLoading && (
        <div
          style={{
            background: 'white',
            borderRadius: '8px',
            padding: '80px 20px',
            textAlign: 'center',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          }}
        >
          <Calendar
            size={56}
            style={{
              marginBottom: '16px',
              color: 'var(--color-gray-300)',
            }}
          />
          <p
            style={{
              color: 'var(--color-gray-600)',
              fontSize: '16px',
              fontWeight: '500',
              marginBottom: '8px',
            }}
          >
            퇴사일을 선택한 뒤 '검색'을 눌러 퇴사자 목록을 조회하세요.
          </p>
          <p style={{ color: 'var(--color-gray-400)', fontSize: '13px' }}>
            기간 내 퇴사한 직원 목록이 표시됩니다.
          </p>
        </div>
      )}

      {/* 로딩 */}
      {isLoading && (
        <div
          style={{
            background: 'white',
            borderRadius: '8px',
            padding: '80px 20px',
            textAlign: 'center',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          }}
        >
          <Loader size={40} style={{ animation: 'spin 1s linear infinite', color: 'var(--color-primary)' }} />
          <p style={{ marginTop: '16px', color: 'var(--color-gray-600)' }}>퇴사자 목록을 조회 중...</p>
        </div>
      )}

      {/* 검색 결과 */}
      {hasSearched && !isLoading && (
        <>
          {/* 결과 헤더 */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '16px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Users size={20} style={{ color: 'var(--color-gray-600)' }} />
              <span style={{ fontSize: '16px', fontWeight: '600', color: 'var(--color-gray-800)' }}>
                조회 결과:{' '}
                <strong style={{ color: 'var(--color-primary)' }}>{filteredEmployees.length}</strong>명
                {searchText && dateFilteredEmployees.length !== filteredEmployees.length && (
                  <span style={{ fontWeight: '400', color: 'var(--color-gray-500)' }}>
                    {' '}
                    (전체 {dateFilteredEmployees.length}명 중)
                  </span>
                )}
              </span>
            </div>
            <div style={{ fontSize: '13px', color: 'var(--color-gray-500)' }}>
              조회 기간: {dateFrom} ~ {dateTo || dateFrom}
            </div>
          </div>

          {/* 테이블 */}
          <div
            style={{
              background: 'white',
              borderRadius: '8px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              overflow: 'hidden',
            }}
          >
            {filteredEmployees.length === 0 ? (
              <div style={{ padding: '60px', textAlign: 'center', color: 'var(--color-gray-500)' }}>
                {searchText
                  ? '검색 결과가 없습니다. 검색어를 확인해주세요.'
                  : '해당 기간에 퇴사한 직원이 없습니다.'}
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '1000px' }}>
                  <thead>
                    <tr style={{ background: 'var(--color-gray-50)', borderBottom: '1px solid var(--color-gray-200)' }}>
                      <th style={thStyle}>이름</th>
                      <th style={thStyle}>사번</th>
                      <th style={thStyle}>조직(퇴사 당시)</th>
                      <th style={thStyle}>직위</th>
                      <th style={thStyle}>직책</th>
                      <th style={thStyle}>퇴사일</th>
                      <th style={thStyle}>고용형태</th>
                      <th style={thStyle}>퇴사사유</th>
                      <th style={thStyle}>근속일</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredEmployees.map((emp) => {
                      // 근속일 계산
                      const hireDate =
                        emp.employmentSnapshot?.hireDate ||
                        emp.employmentSnapshot?.ngleFirstHireDate ||
                        null;
                      const tenureDays = calculateTenureDays(hireDate, emp.termination?.leaveDate);

                      // 조직명 (orgPathNames의 마지막 값 우선, 없으면 orgUnitName)
                      const orgName =
                        emp.orgSnapshot?.orgPathNames?.length
                          ? emp.orgSnapshot.orgPathNames[emp.orgSnapshot.orgPathNames.length - 1]
                          : emp.orgSnapshot?.orgUnitName || '-';

                      return (
                        <tr
                          key={emp.id}
                          style={{
                            borderBottom: '1px solid var(--color-gray-100)',
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--color-gray-50)')}
                          onMouseLeave={(e) => (e.currentTarget.style.background = 'white')}
                        >
                          {/* 이름 (클릭 시 상세 모달) */}
                          <td style={tdStyle}>
                            <button
                              onClick={() => handleShowDetail(emp)}
                              style={{
                                background: 'none',
                                border: 'none',
                                padding: 0,
                                cursor: 'pointer',
                                color: 'var(--color-primary)',
                                fontWeight: 500,
                                fontSize: '14px',
                                textDecoration: 'none',
                              }}
                              onMouseEnter={(e) => (e.currentTarget.style.textDecoration = 'underline')}
                              onMouseLeave={(e) => (e.currentTarget.style.textDecoration = 'none')}
                            >
                              {emp.basicInfo?.nameKo || '(이름 없음)'}
                            </button>
                          </td>
                          <td style={{ ...tdStyle, color: 'var(--color-gray-600)' }}>
                            {emp.employmentSnapshot?.employeeNumber || '-'}
                          </td>
                          <td style={{ ...tdStyle, color: 'var(--color-gray-700)' }}>{orgName}</td>
                          <td style={{ ...tdStyle, color: 'var(--color-gray-700)' }}>
                            {emp.positionSnapshot?.positionName || '-'}
                          </td>
                          <td style={{ ...tdStyle, color: 'var(--color-gray-700)' }}>
                            {emp.titleSnapshot?.titleName || '-'}
                          </td>
                          <td style={{ ...tdStyle, color: 'var(--color-gray-800)', fontWeight: 500 }}>
                            {emp.termination?.leaveDate || '-'}
                          </td>
                          <td style={{ ...tdStyle, color: 'var(--color-gray-700)' }}>
                            {emp.employmentSnapshot?.employmentTypeName ||
                              emp.employmentSnapshot?.employmentTypeCode ||
                              '-'}
                          </td>
                          <td style={{ ...tdStyle, color: 'var(--color-gray-700)' }}>
                            {emp.termination?.reasonName || emp.termination?.reasonCode || '-'}
                          </td>
                          <td style={tdStyle}>
                            {tenureDays !== null ? `${tenureDays.toLocaleString()}일` : '-'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* 상세 모달 */}
      {selectedEmployee && (
        <DetailModal employee={selectedEmployee} onClose={() => setSelectedEmployee(null)} />
      )}
    </div>
  );
}

// ============================================
// 상세 모달 컴포넌트
// ============================================

interface DetailModalProps {
  employee: TerminatedEmployee;
  onClose: () => void;
}

function DetailModal({ employee, onClose }: DetailModalProps) {
  const { basicInfo, orgSnapshot, positionSnapshot, titleSnapshot, employmentSnapshot, termination } = employee;
  // V70.2: 상세 조회 확장 정보 (목록만 열린 상태면 undefined → 해당 행 미노출)
  const additionalInfo = employee.additionalInfo;

  // 근속일 계산
  const hireDate = employmentSnapshot?.hireDate || employmentSnapshot?.ngleFirstHireDate || null;
  const tenureDays = calculateTenureDays(hireDate, termination?.leaveDate);

  return (
    <ModalOverlay
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClose={onClose}
    >
      <div
        style={{
          background: 'white',
          borderRadius: '12px',
          width: '600px',
          maxHeight: '85vh',
          overflow: 'auto',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* 헤더 */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '20px 24px',
            borderBottom: '1px solid var(--color-gray-200)',
            position: 'sticky',
            top: 0,
            background: 'white',
            zIndex: 1,
          }}
        >
          <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>퇴사자 상세 정보</h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
            }}
          >
            <X size={20} color="var(--color-gray-500)" />
          </button>
        </div>

        {/* 내용 */}
        <div style={{ padding: '24px' }}>
          {/* 기본 정보 섹션 */}
          <Section icon={<User size={18} />} title="기본 정보">
            <InfoRow label="이름 (한글)" value={basicInfo?.nameKo || '-'} />
            <InfoRow label="이메일" value={basicInfo?.email || '-'} />
            <InfoRow label="연락처" value={basicInfo?.phoneNumber || '-'} />
            <InfoRow label="생년월일" value={basicInfo?.birthDate || '-'} />
            {/* V70.2: 주민등록번호 — 기존 직원 상세(PersonalInfoTab)와 동일하게 원문 표시.
                BE가 checkIsAdminLike 로 차단하므로 권한 없으면 hasSensitiveAccess=false → 행 미노출 */}
            {additionalInfo?.hasSensitiveAccess && (
              <InfoRow
                label="주민등록번호"
                value={additionalInfo.residentRegistrationNo || '(미등록)'}
                mono
              />
            )}
          </Section>

          {/* 조직/직위 정보 (퇴사 당시) */}
          <Section icon={<Building2 size={18} />} title="조직/직위 (퇴사 당시)">
            <InfoRow label="조직" value={orgSnapshot?.orgUnitName || '-'} />
            {orgSnapshot?.orgPathNames && orgSnapshot.orgPathNames.length > 0 && (
              <InfoRow label="조직 경로" value={orgSnapshot.orgPathNames.join(' > ')} />
            )}
            <InfoRow label="직위" value={positionSnapshot?.positionName || '-'} />
            <InfoRow label="직책" value={titleSnapshot?.titleName || '-'} />
          </Section>

          {/* 고용 정보 */}
          <Section icon={<Calendar size={18} />} title="고용 정보">
            <InfoRow label="사번" value={employmentSnapshot?.employeeNumber || '-'} />
            <InfoRow
              label="고용형태"
              value={employmentSnapshot?.employmentTypeName || employmentSnapshot?.employmentTypeCode || '-'}
            />
            <InfoRow label="입사일" value={employmentSnapshot?.hireDate || '-'} />
            {/* V62: 파견직은 엔글 최초입사일 미노출 (엔글 소속 이력 없음) */}
            {employmentSnapshot?.employmentTypeCode !== 'DISPATCH' && (
              <InfoRow label="nGle 최초 입사" value={employmentSnapshot?.ngleFirstHireDate || '-'} />
            )}
            {/* V70.2: 파견회사명 — SSoT = users.affiliationName.
                V70.2-FIX: **퇴사 시점 고용형태가 DISPATCH 인 경우에만** 노출한다.
                파견→정규직 전환 후 퇴사한 직원의 affiliationName 은 전환 후 현재 소속('엔글')이라
                과거 파견회사명이 아니므로 표시하지 않는다 (BE 에서 null 로 내려옴). */}
            {additionalInfo?.terminatedAsDispatch && additionalInfo.dispatchCompanyName && (
              <InfoRow label="파견회사명" value={additionalInfo.dispatchCompanyName} />
            )}
            <InfoRow label="계약 종료일" value={employmentSnapshot?.contractEndDate || '-'} />
            <InfoRow
              label="근속일"
              value={tenureDays !== null ? `${tenureDays.toLocaleString()}일` : '-'}
              highlight
            />
          </Section>

          {/* 퇴사 정보 */}
          <Section icon={<FileText size={18} />} title="퇴사 정보">
            <InfoRow label="퇴사일" value={termination?.leaveDate || '-'} highlight />
            <InfoRow label="퇴사 사유" value={termination?.reasonName || termination?.reasonCode || '-'} />
            <InfoRow
              label="처리 방식"
              value={
                termination?.terminationSource === 'batch'
                  ? '자동 처리 (배치)'
                  : termination?.terminationSource === 'backfill'
                  ? '데이터 이관 (백필)'
                  : '수동 처리'
              }
            />
            <InfoRow label="처리자" value={termination?.terminatedByName || '-'} />
            {/* V70.2: 퇴사 코멘트 — 고용관리 "퇴사 예약" 시 입력한 terminationReasonNote.
                최대 200자로 한 줄 InfoRow 에는 길어 별도 멀티라인 영역으로 표시한다.
                (기존에는 "사유 상세" 한 줄 필드였고 값이 없으면 행 자체가 사라졌다) */}
            <div style={{ padding: '8px 0' }}>
              <div style={{ color: 'var(--color-gray-500)', fontSize: '14px', marginBottom: '6px' }}>
                퇴사 코멘트
              </div>
              <div
                style={{
                  background: 'white',
                  border: '1px solid var(--color-gray-200)',
                  borderRadius: '6px',
                  padding: '10px 12px',
                  fontSize: '14px',
                  color: termination?.reasonNote ? 'var(--color-gray-900)' : 'var(--color-gray-500)',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  minHeight: '38px',
                }}
              >
                {termination?.reasonNote || '-'}
              </div>
            </div>
          </Section>

          {/* V66: 연도별 휴가 현황 (입사년도 ~ 퇴사년도) */}
          {employee.id && termination?.leaveDate && (employmentSnapshot?.hireDate || employmentSnapshot?.ngleFirstHireDate) && (
            <Section icon={<Calendar size={18} />} title="연도별 휴가 현황">
              <YearLeaveStatus
                employeeId={employee.id}
                hireDate={employmentSnapshot?.hireDate || employmentSnapshot?.ngleFirstHireDate || ''}
                leaveDate={termination.leaveDate}
              />
            </Section>
          )}

          {/* V284: 증명서 기능 placeholder 비노출 (추후 개발 예정) */}
        </div>
      </div>
    </ModalOverlay>
  );
}

// ============================================
// V66: 연도별 휴가 현황 (퇴사자용)
// SSoT: getLeaveStatusByYearForEmployee callable (leaveEntitlements + leaveHistories)
// ============================================

interface YearLeaveStatusProps {
  employeeId: string;
  hireDate: string;   // YYYY-MM-DD
  leaveDate: string;  // YYYY-MM-DD (퇴사일 = referenceDate)
}

interface YearLeaveStatusResponse {
  success: boolean;
  employeeId: string;
  year: number;
  referenceDate: string;
  annual: {
    granted: number;
    used: number;
    preUsed: number;
    remaining: number;
    validFrom: string;
    validUntil: string;
    monthlyUsage: Record<number, number>;
  };
  personalHealing: {
    q1Used: boolean;
    q2Used: boolean;
    q3Used: boolean;
    q4Used: boolean;
    quarterlyUsage: Record<number, number>;
  };
  meta: { entitlementCount: number; hasReferenceCap: boolean; effectiveEnd: string };
}

function YearLeaveStatus({ employeeId, hireDate, leaveDate }: YearLeaveStatusProps) {
  const hireYear = parseInt(hireDate.slice(0, 4), 10);
  const leaveYear = parseInt(leaveDate.slice(0, 4), 10);
  const validRange = !isNaN(hireYear) && !isNaN(leaveYear) && hireYear <= leaveYear;

  const [year, setYear] = useState<number>(validRange ? leaveYear : new Date().getFullYear());
  const [data, setData] = useState<YearLeaveStatusResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  React.useEffect(() => {
    if (!validRange) return;
    let abort = false;
    const fetchYear = async () => {
      setLoading(true);
      setErr(null);
      try {
        if (!functions) throw new Error('functions 미초기화');
        const fn = httpsCallable<
          { employeeId: string; year: number; referenceDate: string },
          YearLeaveStatusResponse
        >(functions, 'getLeaveStatusByYearForEmployee');
        const r = await fn({ employeeId, year, referenceDate: leaveDate });
        if (!abort) setData(r.data);
      } catch (e: any) {
        if (!abort) setErr(e?.message || '조회 실패');
      } finally {
        if (!abort) setLoading(false);
      }
    };
    fetchYear();
    return () => { abort = true; };
  }, [employeeId, year, leaveDate, validRange]);

  if (!validRange) {
    return <div style={{ color: 'var(--color-gray-500)', fontSize: 12 }}>입사일/퇴사일이 유효하지 않아 조회 불가</div>;
  }

  const months = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
  const quarters = [1, 2, 3, 4];
  const annual = data?.annual;
  const ph = data?.personalHealing;

  return (
    <div>
      {/* 연도 네비게이터 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <button
          onClick={() => setYear(y => Math.max(hireYear, y - 1))}
          disabled={year <= hireYear}
          style={{
            background: 'none', border: '1px solid var(--color-gray-300)', borderRadius: 6,
            cursor: year <= hireYear ? 'not-allowed' : 'pointer',
            padding: '4px 8px', opacity: year <= hireYear ? 0.4 : 1,
          }}
        >
          <ChevronLeft size={16} />
        </button>
        <select
          value={year}
          onChange={(e) => setYear(parseInt(e.target.value, 10))}
          style={{ padding: '4px 8px', border: '1px solid var(--color-gray-300)', borderRadius: 6, fontSize: 13 }}
        >
          {Array.from({ length: leaveYear - hireYear + 1 }, (_, i) => hireYear + i).map(y => (
            <option key={y} value={y}>{y}년</option>
          ))}
        </select>
        <button
          onClick={() => setYear(y => Math.min(leaveYear, y + 1))}
          disabled={year >= leaveYear}
          style={{
            background: 'none', border: '1px solid var(--color-gray-300)', borderRadius: 6,
            cursor: year >= leaveYear ? 'not-allowed' : 'pointer',
            padding: '4px 8px', opacity: year >= leaveYear ? 0.4 : 1,
          }}
        >
          <ChevronRight size={16} />
        </button>
        {data?.meta.hasReferenceCap && (
          <span style={{ fontSize: 11, color: 'var(--color-gray-500)' }}>
            ※ 집계 종료일: {data.meta.effectiveEnd} (퇴사일 cap)
          </span>
        )}
      </div>

      {loading && <div style={{ fontSize: 12, color: 'var(--color-gray-500)' }}>조회 중...</div>}
      {err && <div style={{ fontSize: 12, color: '#dc2626' }}>{err}</div>}

      {!loading && !err && annual && (
        <>
          {/* 요약 */}
          <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse', marginBottom: 8 }}>
            <thead>
              <tr style={{ background: 'var(--color-gray-100)' }}>
                <th style={yearLeaveTh}>발생</th>
                <th style={yearLeaveTh}>사용</th>
                <th style={yearLeaveTh}>선사용</th>
                <th style={yearLeaveTh}>잔여</th>
                <th style={yearLeaveTh}>적용 시작</th>
                <th style={yearLeaveTh}>적용 종료</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={yearLeaveTd}>{annual.granted}</td>
                <td style={yearLeaveTd}>{annual.used}</td>
                <td style={yearLeaveTd}>{annual.preUsed}</td>
                <td style={yearLeaveTd}>{annual.remaining}</td>
                <td style={yearLeaveTd}>{annual.validFrom || '-'}</td>
                <td style={yearLeaveTd}>{annual.validUntil || '-'}</td>
              </tr>
            </tbody>
          </table>

          {/* 월별 */}
          <div style={{ fontSize: 11, color: 'var(--color-gray-700)', marginBottom: 4 }}>월별 사용</div>
          <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse', marginBottom: 8 }}>
            <thead>
              <tr style={{ background: 'var(--color-gray-100)' }}>
                {months.map(m => <th key={m} style={yearLeaveTh}>{m}월</th>)}
              </tr>
            </thead>
            <tbody>
              <tr>
                {months.map(m => <td key={m} style={yearLeaveTd}>{annual.monthlyUsage?.[m] || 0}</td>)}
              </tr>
            </tbody>
          </table>

          {/* 퍼스널 힐링데이 */}
          <div style={{ fontSize: 11, color: 'var(--color-gray-700)', marginBottom: 4 }}>퍼스널 힐링데이 (분기별)</div>
          <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--color-gray-100)' }}>
                {quarters.map(q => <th key={q} style={yearLeaveTh}>Q{q}</th>)}
              </tr>
            </thead>
            <tbody>
              <tr>
                {quarters.map(q => {
                  const used = ph?.quarterlyUsage?.[q] || 0;
                  return <td key={q} style={yearLeaveTd}>{used > 0 ? `사용 (${used})` : '-'}</td>;
                })}
              </tr>
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}

const yearLeaveTh: React.CSSProperties = {
  border: '1px solid var(--color-gray-300)', padding: '4px 6px', textAlign: 'center', fontWeight: 500,
};
const yearLeaveTd: React.CSSProperties = {
  border: '1px solid var(--color-gray-300)', padding: '4px 6px', textAlign: 'center',
};

// ============================================
// 유틸 컴포넌트
// ============================================

function Section({ icon, title, children }: { icon: React.ReactNode; title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: '24px' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          marginBottom: '12px',
          color: 'var(--color-gray-700)',
        }}
      >
        {icon}
        <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 600 }}>{title}</h3>
      </div>
      <div
        style={{
          background: 'var(--color-gray-50)',
          borderRadius: '8px',
          padding: '12px 16px',
        }}
      >
        {children}
      </div>
    </div>
  );
}

function InfoRow({
  label,
  value,
  highlight,
  mono,
}: {
  label: string;
  value: string;
  highlight?: boolean;
  /** V70.2: 주민등록번호 등 고정폭 표기 (기존 직원 상세 InfoItem mono 와 동일 목적) */
  mono?: boolean;
}) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        gap: '12px',
        padding: '8px 0',
        borderBottom: '1px solid var(--color-gray-100)',
      }}
    >
      <span style={{ color: 'var(--color-gray-500)', fontSize: '14px', whiteSpace: 'nowrap' }}>{label}</span>
      <span
        style={{
          color: highlight ? '#dc2626' : 'var(--color-gray-900)',
          fontSize: '14px',
          fontWeight: highlight ? 600 : 400,
          fontFamily: mono ? 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace' : undefined,
          textAlign: 'right',
          wordBreak: 'break-word',
        }}
      >
        {value}
      </span>
    </div>
  );
}

// ============================================
// 스타일
// ============================================

const thStyle: React.CSSProperties = {
  padding: '12px 16px',
  textAlign: 'center',
  fontSize: '13px',
  fontWeight: 600,
  color: 'var(--color-gray-600)',
  whiteSpace: 'nowrap',
};

const tdStyle: React.CSSProperties = {
  padding: '12px 16px',
  fontSize: '14px',
  color: 'var(--color-gray-700)',
  textAlign: 'center',
};

export default HrTerminatedEmployeesPage;
